import moment from "moment";
import {
  Address,
  AddressComponents,
  defaultSelectedAddress,
  dummyAddress,
} from "../types/LocationTypes";
import { RegistrationEnums } from "../types/ApplicationTypes";
import { downloadContentImage } from "../services/RegistrationService";
import { base64ToBlob } from "../services";
import { PersonCreationData } from "../types/ServiceUserCreationTypes";
import { IServiceUserInfo } from "../types/ServiceUserInfo";
import { batchDownloadImageContent } from "../services/GroceryService";
import { BatchFetchImagePayload } from "../types/GroceryTypes";

export function constructAddressByPlaceDetails(
  addressDetails: Address,
  addressComponents: AddressComponents[]
) {
  addressComponents.forEach((component: AddressComponents) => {
    const addressType = component.types[0];

    switch (addressType) {
      case "street_number":
        addressDetails.address1 = component.short_name;
        break;
      case "route":
        addressDetails.address1 = `${addressDetails.address1 || ""} ${
          component.short_name
        }`;
        break;
      case "locality":
      case "administrative_area_level_3":
        if (!addressDetails.city) {
          addressDetails.city = component.short_name;
        }
        break;
      case "administrative_area_level_2":
        addressDetails.county = component.long_name;
        break;
      case "administrative_area_level_1":
        addressDetails.state = component.short_name;
        break;
      case "sublocality":
        // Assuming 'city' might be set by 'locality' or 'administrative_area_level_3'
        addressDetails.city = addressDetails.city || component.short_name;
        break;
      case "subpremise":
        addressDetails.address2 = component.short_name;
        break;
      case "postal_code":
        addressDetails.zip = component.long_name;
        break;
      default:
        break;
    }
  });

  addressDetails.fullAddress = constructFullAddress(addressDetails);

  return { addressDetails };
}

export function constructFullAddress(address: Address) {
  const { address1, city, state, zip } = address;
  return [address1, city, state, zip]
    .filter((addressPart: string) => Boolean(addressPart))
    .join(",");
}

export function getInitialAddress(address: Address) {
  const isDummyAddress =
    dummyAddress.address1 === address.address1 ||
    [dummyAddress.zip, ""].includes(address.zip);

  if (!isDummyAddress) {
    return {
      fullAddress: constructFullAddress(address),
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: address.state,
      zip: address.zip,
      county: address.county,
    };
  } else {
    return defaultSelectedAddress;
  }
}

export function capitalizeWords(words: string | null) {
  if (words) {
    return words
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } else {
    return "";
  }
}

export function formatAddress(address: Address) {
  if (address) {
    const formattedAddress = {
      address1: capitalizeWords(address.address1),
      address2: capitalizeWords(address.address2) || null,
      city: capitalizeWords(address.city),
      state: address.state,
      zip: address.zip,
      county: capitalizeWords(address.county),
    };
    return {
      fullAddress: constructFullAddress(formattedAddress),
      ...formattedAddress,
    };
  } else {
    return null;
  }
}

export function getInitialDateOfBirth(dateOfBirth: string) {
  return moment.utc(dateOfBirth).format("YYYY-MM-DD") ===
    RegistrationEnums.DateOfBirth
    ? undefined
    : moment.utc(dateOfBirth);
}

export function getInitialWandaAccommodations(
  wandaAccommodations: string | undefined
) {
  if (wandaAccommodations) {
    const {
      PersonAttributesKeyId,
      WheelChairTravel,
      WheelChairPresent,
      WCToVehicleTransfer,
    } = JSON.parse(wandaAccommodations);

    return {
      PersonAttributesKeyId,
      wheelChairTravel: WheelChairTravel,
      wheelChairPresent: WheelChairPresent,
      wcToVehicleTransfer: WCToVehicleTransfer,
    };
  } else {
    return {
      personAttributesKeyId: 0,
      wheelChairTravel: false,
      wheelChairPresent: false,
      wcToVehicleTransfer: false,
    };
  }
}

export function validateAddressObject(address: Address) {
  const { address1, city, state, zip } = address;
  return [address1, city, state, zip].every(Boolean) && zip.length === 5;
}

export function isValidPhone(phone: string) {
  return phone && phone.replace(/\D/g, "").length === 10;
}

export async function getImageUrl(imageKey: string) {
  try {
    const { fileData, contentType, filename } = await downloadContentImage(
      imageKey
    );
    const base64File = fileData.split("base64,")[1];
    const parsedFile = base64ToBlob(base64File, contentType);
    const file = new File([parsedFile], filename, {
      type: contentType,
    });

    return URL.createObjectURL(file);
  } catch (error) {
    throw error;
  }
}

export function extractBase64File(fileData: string): string {
  if (fileData.includes("base64")) return fileData.split("base64,")[1];
  return fileData;
}

export async function batchFetchImageUrls(data: BatchFetchImagePayload) {
  try {
    const imageUrls = new Map<string, string>();

    const imageContentBatch = await batchDownloadImageContent(data);

    imageContentBatch.forEach((imageContent, index) => {
      const { fileData, contentType, filename } = imageContent;
      const base64File = extractBase64File(fileData);
      const parsedFile = base64ToBlob(base64File, contentType);
      const file = new File([parsedFile], filename, {
        type: contentType,
      });

      imageUrls.set(data.imageKeys[index], URL.createObjectURL(file));
    });

    return imageUrls;
  } catch (error) {
    throw error;
  }
}

export function getBackPersonFormValues(
  serviceUserCreationDetails: PersonCreationData
) {
  const {
    firstName,
    lastName,
    dateOfBirth,
    personAttributes,
    wandaAccommodations,
  } = serviceUserCreationDetails.createPersonData;
  const homeAddress = getInitialAddress(personAttributes.homeAddress);
  const mailingAddress = getInitialAddress(personAttributes.mailingAddress);
  const {
    primaryPhone,
    wandaEmail,
    wandaRelation,
  } = personAttributes.personContact;

  return {
    firstName,
    lastName,
    phone: primaryPhone,
    email: wandaEmail,
    relationship: wandaRelation,
    dateOfBirth,
    photo: serviceUserCreationDetails.createPersonStatusInfo.imageUrl,
    photoFile: serviceUserCreationDetails.createPersonStatusInfo.imageFile,
    isSameAsHomeAddress:
      JSON.stringify(homeAddress) === JSON.stringify(mailingAddress),
    homeAddress,
    mailingAddress,
    wandaAccommodations,
  };
}

export function getServiceUserDetails(
  personId: number,
  serviceUsersList: IServiceUserInfo[]
): IServiceUserInfo | undefined {
  return serviceUsersList.find(
    (serviceUser: { personId: number }) => serviceUser.personId === personId
  );
}

export function getRelationshipNameByRelationshipId(
  relationshipList: { value: number; label: string }[],
  relationshipId: number
) {
  return (
    relationshipList.find(
      (relationshipType: { value: number }) =>
        relationshipType.value === relationshipId
    )?.label || ""
  );
}

export function formatPhoneNumber(phone: string) {
  const phoneNumber = phone.replace(/\D/g, "");
  const match = phoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

export function calculateAge(dateOfBirth: string): number {
  const birthDate = moment(dateOfBirth);
  const today = moment();
  const age = today.diff(birthDate, "years");
  return age;
}
